import React, { useState, useEffect, useCallback } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { feedService } from '../../services/feed';
import { authService } from '../../services/auth';
import { activityDisplayName, isActivityHidden } from '../../services/activityVisibility';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import './Feed.css';

const QUICK_EMOJIS = ['🔥', '😂', '🤡', '💀', '🧠', '😬', '💰', '🎯'];

const TYPE_ICONS = {
  betting: '🎯', food: '🍽️', game: '🎮', quiz: '🧠', vote: '🗳️',
  transport: '🚀', accommodation: '🏨', custom: '📋',
};
const TRANSPORT_ICONS = { train: '🚆', flight: '✈️', car: '🚗', boat: '⛵', bus: '🚌', taxi: '🚕', walk: '🚶', bike: '🚲' };
const ACCOMMODATION_ICONS = { hotel: '🏨', hostel: '🛏️', airbnb: '🏠', camping: '⛺', resort: '🏖️', cabin: '🏡' };

function getActivityIcon(act) {
  if (!act) return '📌';
  if (act.image && !act.image.startsWith('data:')) return act.image;
  if (act.type === 'transport') return TRANSPORT_ICONS[act.transportMode] || '🚀';
  if (act.type === 'accommodation') return ACCOMMODATION_ICONS[act.accommodationType] || '🏨';
  return TYPE_ICONS[act.type] || '📋';
}

function ActivityRefCard({ activity, obfuscated, onOpen }) {
  if (!activity) return null;
  const displayName = obfuscated ? (activity.hint?.trim() || 'Hidden activity') : activity.name;
  return (
    <button type="button" className="feed-item-activity-ref" onClick={onOpen} title="Open activity">
      <span className="feed-item-activity-ref-icon">{obfuscated ? '🙈' : getActivityIcon(activity)}</span>
      <span className="feed-item-activity-ref-body">
        <span className="feed-item-activity-ref-name">{displayName}</span>
        {activity.date && (
          <span className="feed-item-activity-ref-date">
            {obfuscated ? 'Hidden' : new Date(activity.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </span>
      <span className="feed-item-activity-ref-arrow">→</span>
    </button>
  );
}

function FeedItem({ item, currentUserId, onUpdate, onError, activity, activityObfuscated, onOpenActivity }) {
  const allUsers = authService.getAllUsers();
  const itemUser = allUsers.find((u) => u.id === item.userId);
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState('');

  const handleReaction = async (emoji) => {
    try {
      await feedService.addReaction(item.id, currentUserId, emoji);
      await onUpdate();
    } catch (err) {
      onError?.(err.message || 'Could not save reaction');
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      await feedService.addComment(item.id, currentUserId, commentText.trim());
      setCommentText('');
      setShowComment(false);
      await onUpdate();
    } catch (err) {
      onError?.(err.message || 'Could not save comment');
    }
  };

  // Group reactions
  const emojiCounts = {};
  (item.reactions || []).forEach((r) => {
    if (!emojiCounts[r.emoji]) emojiCounts[r.emoji] = { count: 0, mine: false };
    emojiCounts[r.emoji].count++;
    if (r.userId === currentUserId) emojiCounts[r.emoji].mine = true;
  });

  const comments = item.comments || [];

  return (
    <div className="feed-item">
      <div className="feed-item-top">
        <Avatar user={itemUser} size={18} className="feed-item-avatar" />
        <div className="feed-item-meta">
          <strong className="feed-item-author">{itemUser?.displayName || 'Unknown'}</strong><TeamTag user={itemUser} />
          <span className="feed-item-time">
            {new Date(item.createdAt).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        </div>
        {item.activityId && (
          <span className="feed-item-activity-badge">{item.type}</span>
        )}
      </div>

      {activity && <ActivityRefCard activity={activity} obfuscated={activityObfuscated} onOpen={onOpenActivity} />}

      <div className="feed-item-content">{item.content}</div>

      {/* Reactions */}
      {Object.keys(emojiCounts).length > 0 && (
        <div className="feed-reactions">
          {Object.entries(emojiCounts).map(([emoji, data]) => (
            <button
              key={emoji}
              className={`feed-reaction-badge ${data.mine ? 'feed-reaction-badge--mine' : ''}`}
              onClick={() => handleReaction(emoji)}
            >
              {emoji} {data.count > 1 && <span>{data.count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div className="feed-comments">
          {comments.map((c) => {
            const cUser = allUsers.find((u) => u.id === c.userId);
            return (
              <div key={c.id} className="feed-comment">
                <Avatar user={cUser} size={18} className="feed-comment-avatar" />
                <span className="feed-comment-text">
                  <strong>{cUser?.displayName || 'Unknown'}</strong> {c.text}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* React bar */}
      <div className="feed-react-bar">
        {QUICK_EMOJIS.map((emoji) => (
          <button key={emoji} className="feed-react-btn" onClick={() => handleReaction(emoji)} title={emoji}>
            {emoji}
          </button>
        ))}
        <button className="feed-react-btn feed-react-btn--comment" onClick={() => setShowComment(!showComment)}>
          💬
        </button>
      </div>

      {showComment && (
        <div className="feed-comment-input-row">
          <input
            type="text"
            className="feed-comment-input"
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleComment()}
            autoFocus
          />
          <button className="feed-comment-send" onClick={handleComment} disabled={!commentText.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

export default function Feed() {
  const { masterEvent, activities, refreshData, isEventAdmin, isActivityAdmin } = useMasterEvent();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: meId } = useParams();
  const [feedItems, setFeedItems] = useState([]);
  const [postText, setPostText] = useState('');
  const [filterActivity, setFilterActivity] = useState('all');
  const [feedError, setFeedError] = useState('');

  const activityById = {};
  activities.forEach((a) => { activityById[a.id] = a; });
  const canSeeObfuscatedDetails = isActivityAdmin || isEventAdmin;

  const loadFeed = useCallback(async () => {
    if (!masterEvent) return;
    const items = await feedService.getForMasterEvent(masterEvent.id);
    setFeedItems(items);
  }, [masterEvent]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const handlePost = async () => {
    if (!postText.trim() || !masterEvent || !user) return;
    setFeedError('');
    try {
      await feedService.post({
        masterEventId: masterEvent.id,
        type: 'announcement',
        userId: user.id,
        content: postText.trim(),
      });
      setPostText('');
      await loadFeed();
      await refreshData();
    } catch (err) {
      setFeedError(err.message || 'Could not post to feed');
    }
  };

  const filteredItems = filterActivity === 'all'
    ? feedItems
    : feedItems.filter((f) => f.activityId === filterActivity);

  return (
    <div className="feed">
      <h2 className="feed-title">Feed</h2>
      <p className="feed-intro">
        Comments left on individual activities show up here, alongside group announcements.
        Each post links back to the activity it belongs to — click the activity card to open it.
      </p>

      {feedError && <div className="feed-error">{feedError}</div>}

      {/* Composer */}
      <div className="feed-composer">
        <div className="feed-composer-row">
          <Avatar user={user} size={18} className="feed-composer-avatar" />
          <textarea
            className="feed-composer-input"
            placeholder="Share something with the group..."
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            rows={2}
          />
        </div>
        <div className="feed-composer-actions">
          <button
            className="feed-composer-submit"
            onClick={handlePost}
            disabled={!postText.trim()}
          >
            Post
          </button>
        </div>
      </div>

      {/* Filter */}
      {activities.length > 0 && (
        <div className="feed-filter">
          <label className="feed-filter-label">Filter:</label>
          <select
            className="feed-filter-select"
            value={filterActivity}
            onChange={(e) => setFilterActivity(e.target.value)}
          >
            <option value="all">All Activity</option>
            {activities.map((act) => (
              <option key={act.id} value={act.id}>{activityDisplayName(act, masterEvent, canSeeObfuscatedDetails)}</option>
            ))}
          </select>
        </div>
      )}

      {/* Feed Items */}
      <div className="feed-list">
        {filteredItems.length === 0 ? (
          <div className="feed-empty">
            <p>No posts yet. Be the first to share something!</p>
          </div>
        ) : (
          filteredItems.map((item) => (
            <FeedItem
              key={item.id}
              item={item}
              currentUserId={user?.id}
              onUpdate={loadFeed}
              onError={setFeedError}
              activity={item.activityId ? activityById[item.activityId] : null}
              activityObfuscated={isActivityHidden(activityById[item.activityId], masterEvent, canSeeObfuscatedDetails)}
              onOpenActivity={() => item.activityId && navigate(`/me/${meId}/activity/${item.activityId}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}
